import React from 'react';
import { Link, useParams, useLocation } from 'react-router-dom';
import { ArrowRight, Check, X, AlertCircle } from 'lucide-react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { DecisionFramework } from '../components/DecisionFramework';
import { useLanguage } from '../i18n/LanguageContext';
import { getOffer, pick, withSubId, type Lang } from '../offers/config';
import { videoFor } from '../offers/videos';
import { highlightsFor } from '../offers/highlights';
import { goPath, withQuery } from '../offers/links';

// true  -> el boton va a /go/:slug (ruta interna, dispara AffiliateClick)
// false -> el boton va directo al link de impact (una peticion menos,
//          resiste si el JS de la SPA falla)
const USE_GO_ROUTE = true;
const UI = {
  es: {
    notFound: 'Esta pagina no existe',
    backHome: 'Volver al inicio',
    updated: 'Actualizado',
    readTime: (n: number) => `Lectura de ${n} min`,
    confidence: 'Nivel de Confianza',
    disclosureLabel: 'Divulgacion:',
    disclosure: 'Blinkblis recibe una comision si abres una cuenta a traves de los enlaces de esta pagina. No te cuesta nada adicional y no cambia lo que recomendamos.',
    disclosureLink: 'Como trabajamos',
    problemTitle: 'El problema real',
    problem: ['Si llegaste a Estados Unidos hace poco, o simplemente nunca pediste credito, estas en una situacion que el sistema no resuelve bien: para que te den credito necesitas historial, y para tener historial necesitas credito.', 'No es un problema de ingresos. Puedes ganar bien, pagar todo a tiempo y en efectivo, y aun asi aparecer como invisible ante los buros.', 'En Estados Unidos pagar bien no construye historial. Solo construye historial aquello que se reporta.', 'Esa es la unica puerta: necesitas una cuenta que reporte.'],
    bestForTitle: 'Para quien puede ser esto',
    bestForIntro: 'Esto puede encajar contigo si:',
    glanceTitle: 'Kovo en resumen',
    glanceBestFor: 'Recomendado para',
    glanceNotFor: 'No recomendado para',
    howTitle: 'Como funciona, en 3 pasos',
    step: 'Paso',
    whatTitle: 'Que hace realmente un credit builder',
    what: 'Un producto de credit builder existe para una sola cosa: crear actividad que se reporte a los buros de credito, sin exigirte historial previo para abrirla. Abres una cuenta pequena, haces pagos mensuales, y el proveedor los reporta. Lo que se construye ahi es historial de pagos, el factor de mayor peso en la mayoria de los modelos de puntaje.',
    notDoTitle: 'Lo que NO hace',
    notDo: ['No borra ni corrige informacion negativa existente', 'No garantiza aprobacion en ningun producto futuro', 'No produce resultados inmediatos', 'No sustituye pagar tus otras obligaciones a tiempo'],
    notDoNote: 'Cualquiera que te prometa un numero de puntos en un plazo determinado te esta ofreciendo algo que no puede entregar. Los resultados dependen de tu situacion particular y nadie puede garantizarlos.',
    videoTitle: 'Explicado en video',
    videoNote: 'Video producido por Blinkblis.',
    prosConsTitle: 'A favor y en contra',
    pros: 'A favor',
    cons: 'En contra',
    affiliateNote: (network: string) => `Enlace de afiliado via ${network}. Blinkblis recibe una comision si abres una cuenta. Tu costo es el mismo.`,
    notForTitle: 'Para quien NO es esto',
    notForIntro: 'Te lo decimos claro porque preferimos que no abras una cuenta que no necesitas. Esto no es para ti si:',
    faqTitle: 'Preguntas frecuentes',
    criteriaTitle: 'Como lo evaluamos',
    criteria: [{
      t: 'Costo real mensual',
      d: 'Cuanto pagas y que recibes exactamente. Un producto barato que no reporta no sirve de nada.'
    }, {
      t: 'Requisitos de entrada',
      d: 'Pide historial previo? Revision dura? Deposito grande? Menos barreras, mas util para quien empieza.'
    }, {
      t: 'A cuantos buros reporta',
      d: 'Reportar a un solo buro construye una fraccion del historial que construye reportar a todos. Es el criterio que mas separa a un producto de otro.'
    }, {
      t: 'Transparencia y salida',
      d: 'Puedes cancelar sin penalizacion? Los terminos estan claros antes de dar tus datos?'
    }],
    finalTitle: 'Listo para empezar?',
    finalText: 'Toma unos minutos desde el telefono. Si despues de leer esto crees que no es para ti, no lo abras: tambien es una decision valida.',
    footerNote: 'Este contenido es informativo y no constituye asesoria financiera. Los terminos de los productos cambian; verifica siempre directamente con el proveedor. Ver',
    methodology: 'Metodologia',
    disclosurePage: 'Divulgacion de Afiliados',
    and: 'y'
  },
  en: {
    notFound: 'This page does not exist',
    backHome: 'Back to home',
    updated: 'Updated',
    readTime: (n: number) => `${n} min read`,
    confidence: 'Confidence Level',
    disclosureLabel: 'Disclosure:',
    disclosure: 'Blinkblis earns a commission if you open an account through the links on this page. It costs you nothing extra and it does not change what we recommend.',
    disclosureLink: 'How we work',
    problemTitle: 'The real problem',
    problem: ['If you recently arrived in the United States, or you simply never borrowed, you are in a situation the system handles badly: to get credit you need history, and to have history you need credit.', 'It is not an income problem. You can earn well, pay everything on time and in cash, and still be invisible to the bureaus.', 'In the United States, paying well does not build history. Only what gets reported builds history.', 'That is the only door: you need an account that reports.'],
    bestForTitle: 'Who this may be for',
    bestForIntro: 'This may be a fit if:',
    glanceTitle: 'Kovo at a glance',
    glanceBestFor: 'Best for',
    glanceNotFor: 'Not for',
    howTitle: 'How it works, in 3 steps',
    step: 'Step',
    whatTitle: 'What a credit builder actually does',
    what: 'A credit builder product exists for one thing: to create activity that gets reported to the credit bureaus, without requiring prior history to open it. You open a small account, you make monthly payments, and the provider reports them. What gets built there is payment history, the heaviest factor in most scoring models.',
    notDoTitle: 'What it does NOT do',
    notDo: ['It does not erase or correct existing negative information', 'It does not guarantee approval for any future product', 'It does not produce immediate results', 'It does not replace paying your other obligations on time'],
    notDoNote: 'Anyone promising you a specific number of points in a specific timeframe is offering something they cannot deliver. Results depend on your particular situation and nobody can guarantee them.',
    videoTitle: 'Explained on video',
    videoNote: 'Video produced by Blinkblis.',
    prosConsTitle: 'Pros and cons',
    pros: 'Pros',
    cons: 'Cons',
    affiliateNote: (network: string) => `Affiliate link via ${network}. Blinkblis earns a commission if you open an account. Your cost is the same.`,
    notForTitle: 'Who this is NOT for',
    notForIntro: 'We say it plainly because we would rather you not open an account you do not need. This is not for you if:',
    faqTitle: 'Frequently asked questions',
    criteriaTitle: 'How we evaluate it',
    criteria: [{
      t: 'Real monthly cost',
      d: 'What you pay and what exactly you get. A cheap product that does not report is worth nothing.'
    }, {
      t: 'Entry requirements',
      d: 'Does it require prior history? A hard pull? A large deposit? Fewer barriers, more useful for someone starting out.'
    }, {
      t: 'How many bureaus it reports to',
      d: 'Reporting to a single bureau builds a fraction of the history that reporting to all of them does. This is the criterion that separates one product from another the most.'
    }, {
      t: 'Transparency and exit',
      d: 'Can you cancel without penalty? Are the terms clear before you hand over your data?'
    }],
    finalTitle: 'Ready to start?',
    finalText: 'It takes a few minutes from your phone. If after reading this you think it is not for you, do not open it: that is a valid decision too.',
    footerNote: 'This content is informational and does not constitute financial advice. Product terms change; always verify directly with the provider. See',
    methodology: 'Methodology',
    disclosurePage: 'Affiliate Disclosure',
    and: 'and'
  }
};
export function Offer() {
  const {
    slug
  } = useParams();
  const {
    search
  } = useLocation();
  const {
    language
  } = useLanguage();
  const lang = (language === 'en' ? 'en' : 'es') as Lang;
  const t = UI[lang];
  const offer = getOffer(slug);
  if (!offer) {
    return <div className="min-h-screen bg-background text-white flex flex-col" data-id="element-439" data-mp="32iy4d4q1zg21">
        <Navbar data-id="element-440" data-mp="37apwhjouxtua" />
        <div className="flex-1 flex flex-col items-center justify-center gap-6 px-6 text-center" data-id="element-441" data-mp="37bhpqnuzffhe">
          <h1 className="font-display text-3xl" data-id="element-442" data-mp="2tk30w0ee8k2f">{t.notFound}</h1>
          <Link to="/" className="text-accent hover:underline" data-id="element-443" data-mp="2xkddhvwoy8q9">
            {t.backHome}
          </Link>
        </div>
        <Footer data-id="element-444" data-mp="3l9zdumosutzu" />
      </div>;
  }

  // Destino del boton. /go/:slug dispara AffiliateClick y arma el subId.
  const href = USE_GO_ROUTE ? goPath(offer.slug, search) : withSubId(offer.affiliateUrl, offer.slug);
  const facts = pick(offer.facts, lang);
  const bestFor = pick(offer.bestFor, lang);
  const howItWorks = pick(offer.howItWorks, lang);
  const pros = pick(offer.pros, lang);
  const cons = pick(offer.cons, lang);
  const notFor = pick(offer.notFor, lang);
  const faq = pick(offer.faq, lang);
  const video = videoFor(offer.slug);
  const rawHighlights = highlightsFor(offer.slug);
  const highlights = rawHighlights ? pick(rawHighlights, lang) : [];
  const ctaLabel = pick(offer.ctaLabel, lang);
  const ctaMicro = pick(offer.ctaMicro, lang);
  const ctaDisclaimer = pick(offer.ctaDisclaimer, lang);

  // El evento AffiliateClick lo dispara pages/Go.tsx. Si USE_GO_ROUTE
  // esta en false, hay que dispararlo aqui.
  const fireDirect = () => {
    if (USE_GO_ROUTE) return;
    const w = window as any;
    if (typeof w.fbq === 'function') {
      w.fbq('trackCustom', 'AffiliateClick', {
        network: offer.network,
        brand: offer.brand,
        offer: offer.slug
      });
    }
  };
  const Cta = ({
    full = false
  }: {
    full?: boolean;
  }) => <a href={href} rel="nofollow sponsored noopener noreferrer" onClick={fireDirect} className={`${full ? 'w-full' : 'w-full sm:w-auto'} inline-flex items-center justify-center gap-2 bg-accent text-background px-7 h-[56px] rounded-full text-[16px] font-semibold hover:brightness-110 active:scale-[0.98] transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/60`} data-id="element-445" data-mp="1dmeuxfypdmpa">
      {ctaLabel}
      <ArrowRight className="w-[18px] h-[18px]" data-id="element-446" data-mp="3d6cjsiq0puvj" />
    </a>;
  const CtaMicro = () => <p className="mt-3 text-[13px] text-white/35 leading-relaxed" data-id="element-447" data-mp="3qwy50e2y2gwl">{ctaMicro}</p>;
  const Section = ({
    title,
    children,
    id
  }: {
    title: string;
    children: React.ReactNode;
    id?: string;
  }) => <section id={id} className="mb-14" data-id="element-448" data-mp="4o6mhu1o0f30">
      <h2 className="font-display text-[26px] sm:text-3xl font-semibold tracking-[-0.02em] mb-6" data-id="element-449" data-mp="4rb0tk8ugr7e">
        {title}
      </h2>
      {children}
    </section>;
  return <div className="min-h-screen bg-background text-white" data-id="element-450" data-mp="dpmopeibtaw5">
      <Navbar data-id="element-451" data-mp="9o1b1qxywskw" />

      <article className="max-w-[760px] mx-auto px-5 sm:px-6 pt-32 md:pt-40 pb-24" data-id="element-452" data-mp="9pm9vsrbexok">
        {/* ---------- 1. HERO ---------- */}
        <div className="flex items-center gap-2 text-[13px] text-white/40 mb-8" data-id="element-453" data-mp="2fhsx8vnyhs64">
          <Link to={withQuery('/', search)} className="hover:text-white/70 transition-colors" data-id="element-454" data-mp="2bhijgwtx9xgp">
            Blinkblis
          </Link>
          <span data-id="element-455" data-mp="jdl8ap90qb77">/</span>
          <span className="text-accent" data-id="element-456" data-mp="jf63mh35py64">{pick(offer.category, lang)}</span>
        </div>

        <h1 className="font-display text-[34px] sm:text-5xl font-semibold tracking-[-0.03em] leading-[1.08] mb-6" data-id="element-457" data-mp="ocfyic9bf21c">
          {pick(offer.title, lang)}
        </h1>

        {highlights.length > 0 && <div className="flex flex-wrap gap-2 mb-7" data-id="element-458" data-mp="xde777up7xq9">
            {highlights.map(h => <span key={h} className="inline-flex items-center gap-1.5 rounded-full border border-accent/30 bg-accent/[0.07] px-3.5 py-1.5 text-[13px] sm:text-[14px] font-medium text-white/90" data-id="element-459" data-mp="12c7ay8k6oad8">
                <Check className="w-[14px] h-[14px] text-accent shrink-0" data-id="element-460" data-mp="1c54zowfrqvka" />
                {h}
              </span>)}
          </div>}

        <p className="text-[18px] sm:text-xl text-white/55 font-light leading-relaxed mb-8" data-id="element-461" data-mp="1l4rxz9d7bl94">
          {pick(offer.subtitle, lang)}
        </p>

        {/* CTA arriba del pliegue: el trafico pago necesita una accion
            clara en los primeros segundos. */}
        <div className="mb-8" data-id="element-462" data-mp="2wkumugnnczw5">
          <Cta data-id="element-463" data-mp="2wm5oa9tmdvk3" />
          <CtaMicro data-id="element-464" data-mp="2wlmifrh8uxmf" />
        </div>

        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-[13px] text-white/40 mb-8 pb-8 border-b border-white/10" data-id="element-465" data-mp="2wmxjvkn7vtad">
          <span data-id="element-466" data-mp="3pa1j18zmpde8">
            {t.updated}: {pick(offer.updated, lang)}
          </span>
          <span className="text-white/20" data-id="element-467" data-mp="3ka5f19qxllyo">·</span>
          <span data-id="element-468" data-mp="3kbgui6tqoi5m">{t.readTime(offer.readMinutes)}</span>
          <span className="text-white/20" data-id="element-469" data-mp="3fjf83s2uxb1v">·</span>
          <Link to={withQuery('/metodologia', search)} className="text-accent hover:underline" data-id="element-470" data-mp="3fi49s7s6kvi5">
            {t.confidence} {offer.confidence}%
          </Link>
        </div>

        {/* ---------- 2. DIVULGACION ---------- */}
        <div className="mb-14 border-l-2 border-accent bg-white/[0.03] rounded-r-xl px-5 py-4 text-[15px] leading-relaxed text-white/60" data-id="element-471" data-mp="7340sw4mudka">
          <span className="text-white/85 font-medium" data-id="element-472" data-mp="3ntmfjh9f3wbi">
            {t.disclosureLabel}
          </span>{' '}
          {t.disclosure}{' '}
          <Link to={withQuery('/divulgacion-de-afiliados', search)} className="text-accent hover:underline whitespace-nowrap" data-id="element-473" data-mp="3dtvjvc94fb9u">
            {t.disclosureLink} →
          </Link>
        </div>

        {/* ---------- 3. EL PROBLEMA REAL ---------- */}
        <Section title={t.problemTitle} data-id="element-474" data-mp="fbq90idxorpq">
          <div className="space-y-5 text-[17px] leading-[1.75] text-white/65 font-light" data-id="element-475" data-mp="faoprqmrkywx">
            <p data-id="element-476" data-mp="lshbsgspjvs">{t.problem[0]}</p>
            <p data-id="element-477" data-mp="mk36bklc38v">
              {t.problem[1]}{' '}
              <span className="text-white/90" data-id="element-478" data-mp="5ldebxceni5v">{t.problem[2]}</span>
            </p>
            <p data-id="element-479" data-mp="tbhh62hrrdvt">{t.problem[3]}</p>
          </div>
        </Section>

        {/* ---------- 4. PARA QUIEN PUEDE SER ---------- */}
        <Section title={t.bestForTitle} data-id="element-480" data-mp="y2hj5m5qwm56">
          <p className="text-[16px] text-white/50 font-light mb-6" data-id="element-481" data-mp="y4ao8x0pmyb2">
            {t.bestForIntro}
          </p>
          <ul className="grid sm:grid-cols-2 gap-3" data-id="element-482" data-mp="2iqa34pf1as77">
            {bestFor.map(b => <li key={b} className="flex gap-3 items-start rounded-2xl border border-white/10 bg-white/[0.02] px-4 py-3.5 text-[15px] text-white/75 leading-relaxed" data-id="element-483" data-mp="2drflhqssz9oo">
                <Check className="w-[18px] h-[18px] text-accent shrink-0 mt-0.5" data-id="element-484" data-mp="1ztr1smah28l7" />
                {b}
              </li>)}
          </ul>
        </Section>

        {/* ---------- 5. KOVO EN RESUMEN ---------- */}
        <Section title={t.glanceTitle} data-id="element-485" data-mp="28uyoe73mlkzg">
          <div className="rounded-3xl border border-accent/25 bg-accent/[0.04] p-6 sm:p-8" data-id="element-486" data-mp="28w0ad6m6jrvy">
            <div className="flex flex-wrap items-center gap-3 mb-6" data-id="element-487" data-mp="23tstc2mhtlmr">
              <span className="font-display text-3xl font-semibold" data-id="element-488" data-mp="3k8t1cf75ccay">
                {offer.brand}
              </span>
              <span className="text-[12px] font-medium text-accent border border-accent/40 rounded-full px-3 py-1" data-id="element-489" data-mp="3f9psskryjny8">
                {t.confidence} {offer.confidence}%
              </span>
            </div>

            {facts.length > 0 && <div className="grid sm:grid-cols-2 gap-x-8 gap-y-3 mb-6" data-id="element-490" data-mp="2hy0vvqi6cjg7">
                {facts.map(f => <div key={f.label} className="flex items-baseline justify-between gap-4" data-id="element-491" data-mp="2qwm9d78sh8a4">
                    <span className="text-[14px] text-white/45" data-id="element-492" data-mp="1u86in7ty0svf">{f.label}</span>
                    <span className="text-[15px] text-white font-medium text-right" data-id="element-493" data-mp="1y98j1rzljayz">
                      {f.value}
                    </span>
                  </div>)}
              </div>}

            <div className="grid sm:grid-cols-2 gap-4 mb-7 pt-6 border-t border-white/10" data-id="element-494" data-mp="27zl2thzl7042">
              <div className="rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3.5" data-id="element-495" data-mp="1ahusnrl09pgn">
                <p className="text-[12px] uppercase tracking-wider text-accent/90 mb-1.5" data-id="element-496" data-mp="1fgeezt3ih39j">
                  {t.glanceBestFor}
                </p>
                <p className="text-[15px] text-white/80 leading-relaxed" data-id="element-497" data-mp="12z3npi9bfbal">
                  {pick(offer.glanceBestFor, lang)}
                </p>
              </div>
              <div className="rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3.5" data-id="element-498" data-mp="xyfm62ngnc24">
                <p className="text-[12px] uppercase tracking-wider text-white/40 mb-1.5" data-id="element-499" data-mp="txvnuvr5i2ee">
                  {t.glanceNotFor}
                </p>
                <p className="text-[15px] text-white/60 leading-relaxed" data-id="element-500" data-mp="k8vovccnf707">
                  {pick(offer.glanceNotFor, lang)}
                </p>
              </div>
            </div>

            <Cta data-id="element-501" data-mp="f76b3oy1kf8f" />
            <CtaMicro data-id="element-502" data-mp="aapmyuque0vc" />
            <p className="mt-4 pt-4 border-t border-white/10 text-[12px] text-white/30 leading-relaxed" data-id="element-503" data-mp="ab7jztlbwj4b">
              {ctaDisclaimer}
            </p>
          </div>
        </Section>

        {/* ---------- 6. COMO FUNCIONA ---------- */}
        <Section title={t.howTitle} data-id="element-504" data-mp="1ajbfeepfayt">
          <ol className="space-y-3" data-id="element-505" data-mp="1p6pblmo5iu02">
            {howItWorks.map((s, i) => <li key={s.t} className="flex gap-4 rounded-2xl border border-white/10 bg-white/[0.02] p-5" data-id="element-506" data-mp="1p4miuuvm6mrn" data-mpi={i}>
                <span className="shrink-0 w-9 h-9 rounded-full border border-accent/40 bg-accent/10 text-accent font-semibold text-[15px] flex items-center justify-center" data-id="element-507" data-mp="1g4zkkhy6lx2t">
                  {i + 1}
                </span>
                <div data-id="element-508" data-mp="16en1ku60mbys">
                  <h3 className="text-white font-medium text-[16px] mb-1.5" data-id="element-509" data-mp="1b6o8s1obtchy">
                    {s.t}
                  </h3>
                  <p className="text-[15px] text-white/55 leading-relaxed font-light" data-id="element-510" data-mp="wia5xqqwto1e">
                    {s.d}
                  </p>
                </div>
              </li>)}
          </ol>
        </Section>

        {/* Video propio de Blinkblis. Si offers/videos.ts esta vacio,
            esta seccion no se renderiza. */}
        {video && <Section title={t.videoTitle} data-id="element-511" data-mp="si9mfroyemnh">
            <div className="relative w-full rounded-2xl overflow-hidden border border-white/10 bg-black aspect-video" data-id="element-512" data-mp="8tlh89bhp0gz">
              <iframe className="absolute inset-0 w-full h-full" src={`https://www.youtube-nocookie.com/embed/${video}`} title={pick(offer.title, lang)} loading="lazy" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen data-id="element-513" data-mp="3wlm2tg6hi4n" />
            </div>
            <p className="mt-3 text-[13px] text-white/35" data-id="element-514" data-mp="mtmtekx7fubr">{t.videoNote}</p>
          </Section>}

        {/* ---------- 7. A FAVOR Y EN CONTRA ---------- */}
        <Section title={t.prosConsTitle} data-id="element-515" data-mp="wjfw0wfdcz3v">
          <div className="grid sm:grid-cols-2 gap-8" data-id="element-516" data-mp="wkr77hb59950">
            <div data-id="element-517" data-mp="1aj7aey9sshie">
              <h3 className="text-[13px] uppercase tracking-wider text-white/40 mb-4" data-id="element-518" data-mp="1aks5cp00y2gs">
                {t.pros}
              </h3>
              <ul className="space-y-3" data-id="element-519" data-mp="15ikqnrnt88ar">
                {pros.map(p => <li key={p} className="flex gap-2.5 text-[15px] text-white/70 leading-relaxed" data-id="element-520" data-mp="1i1swtl1197dy">
                    <Check className="w-4 h-4 text-accent shrink-0 mt-1" data-id="element-521" data-mp="1s23bzb6moovr" />
                    {p}
                  </li>)}
              </ul>
            </div>
            <div data-id="element-522" data-mp="1n0nrbk8d4kdw">
              <h3 className="text-[13px] uppercase tracking-wider text-white/40 mb-4" data-id="element-523" data-mp="zcmlwk6hdk2p">
                {t.cons}
              </h3>
              <ul className="space-y-3" data-id="element-524" data-mp="ueujgw87mfk9">
                {cons.map(c => <li key={c} className="flex gap-2.5 text-[15px] text-white/70 leading-relaxed" data-id="element-525" data-mp="1937ki7m5molm">
                    <X className="w-4 h-4 text-white/30 shrink-0 mt-1" data-id="element-526" data-mp="149w9duab7lnw" />
                    {c}
                  </li>)}
              </ul>
            </div>
          </div>

          <div className="mt-8 rounded-2xl border border-white/10 bg-white/[0.02] p-6" data-id="element-527" data-mp="bn1rn54vn0e4">
            <div className="flex items-center gap-2 mb-4" data-id="element-528" data-mp="2n93z8cgbp8o7">
              <AlertCircle className="w-[18px] h-[18px] text-accent" data-id="element-529" data-mp="2s1x4d1fpej0t" />
              <h3 className="text-white font-medium text-[15px]" data-id="element-530" data-mp="2s382akmgwwk0">
                {t.notDoTitle}
              </h3>
            </div>
            <ul className="space-y-3" data-id="element-531" data-mp="2iaso7yeaks4e">
              {t.notDo.map(item => <li key={item} className="flex gap-3 text-[15px] text-white/55 leading-relaxed" data-id="element-532" data-mp="361ekytiuipz3">
                  <X className="w-4 h-4 text-white/30 shrink-0 mt-1" data-id="element-533" data-mp="3azpvl6gyt4id" />
                  {item}
                </li>)}
            </ul>
            <p className="mt-5 pt-5 border-t border-white/10 text-[15px] text-white/55 leading-relaxed" data-id="element-534" data-mp="31zb7y0gj3dbp">
              {t.notDoNote}
            </p>
          </div>
        </Section>

        {/* ---------- 8. PARA QUIEN NO ES ---------- */}
        <Section title={t.notForTitle} data-id="element-535" data-mp="1ql5ywt1gkd9i">
          <p className="text-[16px] text-white/50 font-light mb-6" data-id="element-536" data-mp="1qi1qtkkvgxdk">
            {t.notForIntro}
          </p>
          <ul className="space-y-4" data-id="element-537" data-mp="3nah7vtb21dv1">
            {notFor.map(n => <li key={n} className="flex gap-3 text-[16px] text-white/65 leading-relaxed font-light" data-id="element-538" data-mp="3nck44v2sw387">
                <span className="text-accent shrink-0" data-id="element-539" data-mp="3eiynt7azmdwo">—</span>
                {n}
              </li>)}
          </ul>
        </Section>

        {/* ---------- 9. FAQ ---------- */}
        <Section title={t.faqTitle} data-id="element-540" data-mp="34jg4mt2wiw5o">
          <div className="space-y-3" data-id="element-541" data-mp="34iem63jk37dx">
            {faq.map(f => <details key={f.q} className="group rounded-2xl border border-white/10 bg-white/[0.02] overflow-hidden" data-id="element-542" data-mp="2zk4qzsxu06sb">
                <summary className="cursor-pointer list-none px-5 py-4 text-[16px] text-white/85 font-medium flex items-center justify-between gap-4 min-h-[56px]" data-id="element-543" data-mp="xtje64q95ehs">
                  {f.q}
                  <span className="text-accent text-xl shrink-0 group-open:rotate-45 transition-transform duration-200" data-id="element-544" data-mp="tq3o5r8ouce2">
                    +
                  </span>
                </summary>
                <p className="px-5 pb-5 text-[15px] text-white/55 leading-relaxed font-light" data-id="element-545" data-mp="2gmb834skq7op">
                  {f.a}
                </p>
              </details>)}
          </div>
        </Section>

        {/* ---------- 10. METODOLOGIA ---------- */}
        <Section title={t.criteriaTitle} data-id="element-546" data-mp="1ss0da37pxtwe">
          <div className="grid sm:grid-cols-2 gap-4 mb-10" data-id="element-547" data-mp="1st1nk5gwucd2">
            {t.criteria.map(c => <div key={c.t} className="rounded-2xl border border-white/10 bg-white/[0.02] p-5" data-id="element-548" data-mp="21rde67hs1a5i">
                <h3 className="text-white font-medium mb-2 text-[15px]" data-id="element-549" data-mp="26rj85ubir8qr">
                  {c.t}
                </h3>
                <p className="text-[14px] text-white/50 leading-relaxed font-light" data-id="element-550" data-mp="3d60oipyd9emh">
                  {c.d}
                </p>
              </div>)}
          </div>
          <DecisionFramework lang={lang} data-id="element-551" data-mp="3gpj1nzyku4zm" />
          <p className="text-[17px] leading-[1.75] text-white/65 font-light" data-id="element-552" data-mp="3gqkf2b32f3ki">
            {t.what}
          </p>
        </Section>

        {/* ---------- 11. CTA FINAL ---------- */}
        <section className="rounded-3xl border border-white/10 bg-white/[0.02] p-6 sm:p-8 text-center" data-id="element-553" data-mp="3pqh80owhmold">
          <h2 className="font-display text-2xl font-semibold mb-3" data-id="element-554" data-mp="3lm9gkjynaujg">
            {t.finalTitle}
          </h2>
          <p className="text-[15px] text-white/50 mb-7 font-light max-w-md mx-auto leading-relaxed" data-id="element-555" data-mp="3fv6wx7et6qv">
            {t.finalText}
          </p>
          <Cta full data-id="element-556" data-mp="d80u7tmpuzzq" />
          <p className="mt-3 text-[13px] text-white/35 leading-relaxed" data-id="element-557" data-mp="d6g0g6887l2x">
            {ctaMicro}
          </p>
          <p className="mt-5 pt-5 border-t border-white/10 text-[12px] text-white/30 leading-relaxed max-w-lg mx-auto" data-id="element-558" data-mp="896c6twkpfgo">
            {ctaDisclaimer}
          </p>
        </section>

        {/* ---------- 12. PIE LEGAL ---------- */}
        <p className="mt-12 text-[13px] text-white/35 leading-relaxed text-center" data-id="element-559" data-mp="h7i18rhsk570">
          {t.affiliateNote(offer.network)}
        </p>
        <p className="mt-3 text-[13px] text-white/35 leading-relaxed text-center" data-id="element-560" data-mp="15uhecf1zprt3">
          {t.footerNote}{' '}
          <Link to={withQuery('/metodologia', search)} className="text-accent hover:underline" data-id="element-561" data-mp="rtof5h0qu8jn">
            {t.methodology}
          </Link>{' '}
          {t.and}{' '}
          <Link to={withQuery('/divulgacion-de-afiliados', search)} className="text-accent hover:underline" data-id="element-562" data-mp="i3v6oovwe9jm">
            {t.disclosurePage}
          </Link>
          .
        </p>
      </article>

      {/* Barra fija en movil */}
      <div className="sm:hidden fixed bottom-0 left-0 right-0 z-30 px-4 pt-3 pb-4 bg-background/90 backdrop-blur-lg border-t border-white/10" data-id="element-563" data-mp="d50n3kq3wgyg">
        <a href={href} rel="nofollow sponsored noopener noreferrer" onClick={fireDirect} className="w-full inline-flex items-center justify-center gap-2 bg-accent text-background h-[52px] rounded-full text-[16px] font-semibold active:scale-[0.98] transition-transform" data-id="element-564" data-mp="3vbop31pv0nzw">
          {ctaLabel}
          <ArrowRight className="w-[18px] h-[18px]" data-id="element-565" data-mp="1w3yleavffoqy" />
        </a>
        <p className="mt-2 text-[11px] text-white/30 text-center leading-snug" data-id="element-566" data-mp="1w4hr8t7symom">
          {ctaMicro}
        </p>
      </div>

      <Footer data-id="element-567" data-mp="2osx4ba7diuxb" />
    </div>;
}